import {publicFetch, resolvePublicApiBaseUrl} from '@fitro/customer-host/api'
import {rememberGuestPaymentReturn} from '../lib/guest-payment-return'

export type GuestCheckoutSummary = {
    studioName: string
    studioSlug: string
    eventTitle: string
    startsAt: string
    endsAt: string
    timeZone: string
    instructorName: string | null
    location: string | null
    offerName: string | null
    /** The regular price; above `amountMinor` only when `promotionName` lowered it. */
    baseAmountMinor: number
    amountMinor: number
    promotionName: string | null
    /** The benefit card the guest will show on site, when the price is the card price. */
    benefitCardName: string | null
    currency: string
}

export type GuestCheckoutPreview = {
    previewId: string
    expiresAt: string
    termsUrl: string
    privacyUrl: string
    fitroTermsUrl: string
    summary: GuestCheckoutSummary
    firstPurchaseDiscountAvailable: boolean
    /** Set only when a price reduction may be shown: the Omnibus reference to strike through. */
    lowestPrice30dMinor: number | null
    /** The class shows participants split by gender, so the guest must give theirs. */
    genderRequired: boolean
}

export type GuestCheckoutContact = {
    firstName: string
    lastName: string
    email: string
    phone: string
    gender?: 'FEMALE' | 'MALE'
}

export type GuestCheckoutConsents = {
    termsAccepted: boolean
    privacyAccepted: boolean
    fitroTermsAccepted: boolean
    marketingConsent: boolean
}

export type GuestCheckoutCommit = {
    checkoutId: string
    recoveryToken: string
    holdExpiresAt: string
}

/** The part of the payment start response the web reads. */
export type GuestCheckoutPayment = {
    checkoutId: string
    paymentUrl: string
}

export type GuestCheckoutErrorCode =
    | 'booking_refused'
    | 'full'
    | 'registration_closed'
    | 'offer_changed'
    | 'benefit_card_unavailable'
    | 'login_required'
    | 'preview_expired'
    | 'checkout_expired'
    | 'terms_changed'
    | 'terms_required'
    | 'gender_required'
    | 'invalid_contact'
    | 'too_many_holds'
    | 'guest_holds_busy'
    | 'rate_limited'
    | 'recipient_rate_limited'
    | 'not_found'
    | 'payment_unavailable'
    | 'recovery_link_delivery_unavailable'
    | 'payment_already_confirmed'
    | 'unavailable'

/** A Guest Checkout request the backend rejected, with its stable error code. */
export class GuestCheckoutError extends Error {
    constructor(readonly code: GuestCheckoutErrorCode) {
        super(`Guest checkout failed: ${code}`)
        this.name = 'GuestCheckoutError'
    }
}

const KNOWN_CODES = new Set<GuestCheckoutErrorCode>([
    'booking_refused', 'full', 'registration_closed', 'offer_changed', 'benefit_card_unavailable', 'login_required', 'preview_expired',
    'checkout_expired', 'terms_changed', 'terms_required', 'gender_required', 'invalid_contact', 'too_many_holds', 'rate_limited', 'not_found',
    'guest_holds_busy', 'recipient_rate_limited', 'payment_unavailable', 'recovery_link_delivery_unavailable', 'payment_already_confirmed',
])

// The backend refused the commit before saving a Hold, so the next attempt needs a new Idempotency-Key.
// Anything else (network, timeout, 5xx, recovery_link_delivery_unavailable) may follow a saved Hold.
const COMMIT_REFUSALS = new Set<GuestCheckoutErrorCode>([
    'booking_refused', 'full', 'registration_closed', 'offer_changed', 'login_required', 'preview_expired',
    'checkout_expired', 'terms_changed', 'terms_required', 'gender_required', 'invalid_contact', 'too_many_holds', 'not_found',
    'guest_holds_busy', 'recipient_rate_limited',
])

export function isGuestCheckoutCommitRefusal(error: unknown): boolean {
    return error instanceof GuestCheckoutError && COMMIT_REFUSALS.has(error.code)
}

/** `benefitCard`: the card the guest will show on site, when they pick the card price; `variant` only for a variant entry. */
export function previewEventGuestCheckout(
    slug: string,
    publicCode: string,
    benefitCard?: {provider: string, variant?: string | null} | null,
): Promise<GuestCheckoutPreview> {
    return post(
        `/api/public/partners/${encodeURIComponent(slug)}/events/${encodeURIComponent(publicCode)}/guest-checkouts/preview`,
        benefitCard
            ? {benefitProvider: benefitCard.provider, ...(benefitCard.variant ? {benefitVariant: benefitCard.variant} : {})}
            : undefined,
    ).then(parsePreview)
}

export function previewIndividualTrainingGuestCheckout(
    slug: string,
    offerId: string,
    slot: {startAt: string; trainerId?: string | null; locationId?: string | null},
): Promise<GuestCheckoutPreview> {
    return post(
        `/api/public/partners/${encodeURIComponent(slug)}/private-bookings/offers/${encodeURIComponent(offerId)}/guest-checkouts/preview`,
        {startAt: slot.startAt, trainerId: slot.trainerId ?? null, locationId: slot.locationId ?? null},
    ).then(parsePreview)
}

export function commitGuestCheckout(
    slug: string,
    previewId: string,
    contact: GuestCheckoutContact,
    consents: GuestCheckoutConsents,
    idempotencyKey: string,
): Promise<GuestCheckoutCommit> {
    // The contact draft outlives the booking: every guest is invited to an account, and sign-up prefills it.
    return post(
        `/api/public/partners/${encodeURIComponent(slug)}/guest-checkouts/commit`,
        {previewId, ...contact, ...consents},
        {'Idempotency-Key': idempotencyKey},
    ).then(parseCommit)
}

/**
 * Starts or resumes the online payment of a Guest Checkout and returns the provider page. The
 * Guest Management Link token alone authorizes it. The provider returns to `/payment-return?id=`
 * with only the checkout id, so this browser remembers which link that id belongs to.
 */
export async function startGuestCheckoutPayment(recoveryToken: string): Promise<string> {
    const body = record(await post(
        `/api/public/guest-bookings/manage/${encodeURIComponent(recoveryToken)}/payment`,
        {},
    ))
    const checkoutId = stringField(body.checkoutId)
    const paymentUrl = new URL(stringField(body.paymentUrl))
    if (paymentUrl.protocol !== 'https:' || paymentUrl.username || paymentUrl.password || paymentUrl.hash) {
        throw new GuestCheckoutError('unavailable')
    }
    rememberGuestPaymentReturn(checkoutId, recoveryToken)
    return paymentUrl.toString()
}

async function post(path: string, body: unknown, headers: Record<string, string> = {}): Promise<unknown> {
    const baseUrl = resolvePublicApiBaseUrl()
    if (!baseUrl) throw new GuestCheckoutError('unavailable')
    let response: Response
    try {
        response = await publicFetch(`${baseUrl}${path}`, {
            method: 'POST',
            credentials: 'omit',
            cache: 'no-store',
            headers: {'content-type': 'application/json', ...headers},
            body: body === undefined ? undefined : JSON.stringify(body),
        })
    } catch {
        throw new GuestCheckoutError('unavailable')
    }
    if (response.status === 429) {
        const code = await errorCode(response)
        throw new GuestCheckoutError(code === 'too_many_holds' || code === 'recipient_rate_limited' ? code : 'rate_limited')
    }
    if (!response.ok) {
        const code = await errorCode(response)
        throw new GuestCheckoutError(code && KNOWN_CODES.has(code as GuestCheckoutErrorCode) ? code as GuestCheckoutErrorCode : 'unavailable')
    }
    return response.json()
}

async function errorCode(response: Response): Promise<string | null> {
    try {
        const body: unknown = await response.json()
        const code = body && typeof body === 'object' ? (body as {code?: unknown}).code : null
        return typeof code === 'string' ? code.trim().toLowerCase() : null
    } catch {
        return null
    }
}

function record(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new GuestCheckoutError('unavailable')
    return value as Record<string, unknown>
}

function stringField(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new GuestCheckoutError('unavailable')
    return value.trim()
}

function nullableString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null
}

function parsePreview(value: unknown): GuestCheckoutPreview {
    const row = record(value)
    const summary = record(row.summary)
    const amountMinor = summary.amountMinor
    const baseAmountMinor = summary.baseAmountMinor
    if (typeof amountMinor !== 'number' || !Number.isInteger(amountMinor) || amountMinor < 0
        || typeof baseAmountMinor !== 'number' || !Number.isInteger(baseAmountMinor) || baseAmountMinor < amountMinor) {
        throw new GuestCheckoutError('unavailable')
    }
    return {
        previewId: stringField(row.previewId),
        expiresAt: stringField(row.expiresAt),
        termsUrl: stringField(row.termsUrl),
        privacyUrl: stringField(row.privacyUrl),
        fitroTermsUrl: stringField(row.fitroTermsUrl),
        summary: {
            studioName: stringField(summary.studioName),
            studioSlug: stringField(summary.studioSlug),
            eventTitle: stringField(summary.eventTitle),
            startsAt: stringField(summary.startsAt),
            endsAt: stringField(summary.endsAt),
            timeZone: stringField(summary.timeZone),
            instructorName: nullableString(summary.instructorName),
            location: nullableString(summary.location),
            offerName: nullableString(summary.offerName),
            baseAmountMinor,
            amountMinor,
            promotionName: nullableString(summary.promotionName),
            benefitCardName: nullableString(summary.benefitCardName),
            currency: stringField(summary.currency),
        },
        firstPurchaseDiscountAvailable: row.firstPurchaseDiscountAvailable === true,
        lowestPrice30dMinor: Number.isSafeInteger(row.lowestPrice30dMinor) && (row.lowestPrice30dMinor as number) > 0
            ? row.lowestPrice30dMinor as number
            : null,
        genderRequired: row.genderRequired === true,
    }
}

function parseCommit(value: unknown): GuestCheckoutCommit {
    const row = record(value)
    return {
        checkoutId: stringField(row.checkoutId),
        recoveryToken: stringField(row.recoveryToken),
        holdExpiresAt: stringField(row.holdExpiresAt),
    }
}
